'use client'

import { type FC, useEffect, useMemo, useRef, useState } from 'react'
import { type IntlShape, useIntl } from 'react-intl'

import { type NowSlotExtended } from '@redteclab/api/clients/bully'

import { nowBasicCheckRespGetSlotOrderEndDateTimeInIsoBerlinTime } from '../model/nowBasicCheckResponseAccessor'
import { nowFormatCountdownTime } from '../model/nowFormatCountdownTime'

const THOUSAND = 1000
const SIXTY_THOUSAND = 60_000

const setTimerDifference = ({
  endTime,
  intl,
  setTimeLeft,
}: {
  endTime?: Date
  intl: IntlShape
  setTimeLeft: (time: string) => void
}): void => {
  if (!endTime) {
    return
  }

  const now = new Date()
  const difference = endTime.getTime() - now.getTime()

  const distanceDate = new Date(difference)
  const utcDistanceDate = new Date(
    distanceDate.getTime() + distanceDate.getTimezoneOffset() * SIXTY_THOUSAND,
  )

  setTimeLeft(nowFormatCountdownTime(intl, utcDistanceDate))
}

export const NowTimeLeftCounter: FC<{
  className?: string
  nowSlot: NowSlotExtended
}> = ({ className, nowSlot }) => {
  const [timeLeft, setTimeLeft] = useState('--:--:--')
  const intl = useIntl()

  const endTime = useMemo(() => {
    return nowBasicCheckRespGetSlotOrderEndDateTimeInIsoBerlinTime(nowSlot)
  }, [nowSlot])

  const intervalRef = useRef<ReturnType<typeof window.setInterval>>(undefined)

  useEffect(() => {
    // ensure we have only one interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
    // set immediately on render otherwise will set after time specified in interval
    setTimerDifference({ endTime, intl, setTimeLeft })

    intervalRef.current = setInterval(() => {
      setTimerDifference({ endTime, intl, setTimeLeft })
    }, THOUSAND)

    // cleanup interval if any
    return (): void => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [endTime, intl])

  return <span className={className}>{timeLeft}</span>
}
